<section class="business-calendars" aria-labelledby="business-calendars-title">
  <header class="calendar-header">
    <div>
      <h2 id="business-calendars-title">Business calendars</h2>
      <p>Manage calendar versions and preview date calculations.</p>
    </div>
    <div class="calendar-actions">
      <button
        *ngIf="canRead() && canAuthor() && !accessDenied"
        uiButton
        type="button"
        (click)="beginCreate()"
        [disabled]="draftBusy"
      >
        New calendar</button
      ><button
        uiButton
        type="button"
        (click)="refresh()"
        [disabled]="
          loadingCatalog ||
          loadingDrafts ||
          calculating ||
          draftBusy ||
          !canRead()
        "
      >
        {{
          loadingCatalog || loadingDrafts ? "Refreshing…" : "Refresh calendars"
        }}
      </button>
    </div>
  </header>
  <p *ngIf="!canRead() || accessDenied" class="calendar-error" role="alert">
    You do not currently have access to business calendars. Your calculation
    inputs are kept.
  </p>
  <section *ngIf="catalogError as error" class="calendar-error" role="alert">
    <strong>{{ error.title }}</strong>
    <ul>
      <li *ngFor="let message of error.messages">{{ message }}</li>
    </ul>
    <p>Your inputs are kept. Refresh calendars to retry.</p>
  </section>
  <p *ngIf="notice" class="calendar-notice" role="status" aria-live="polite">
    {{ notice }}
  </p>
  <p *ngIf="loadingCatalog" role="status">Loading exact calendar versions…</p>
  <ng-container *ngIf="canRead() && !accessDenied">
    <section class="calendar-management" aria-label="Calendar administration">
      <label *ngIf="openDrafts.length"
        >Open draft
        <select
          uiControl
          [ngModel]="draft?.statusCode === 'draft' ? draft?.id : ''"
          (ngModelChange)="selectDraft($event)"
          [disabled]="loadingDrafts || draftBusy"
        >
          <option value="">Select a saved draft</option>
          <option *ngFor="let saved of openDrafts" [value]="saved.id">
            {{ saved.definition.label }} · version
            {{ saved.definition.versionCode }}
          </option>
        </select></label
      >
      <p *ngIf="loadingDrafts" role="status">Loading saved calendar drafts…</p>
      <form
        *ngIf="showCreate"
        class="calendar-create"
        (ngSubmit)="createCalendar()"
      >
        <label
          >New calendar name
          <input
            uiControl
            name="newCalendarName"
            [(ngModel)]="newCalendarName"
            (ngModelChange)="draftChanged()"
            [disabled]="draftBusy || !canAuthor()"
            maxlength="160"
            required
        /></label>
        <div class="calendar-actions">
          <button
            uiButton
            type="submit"
            severity="primary"
            [outlined]="false"
            [disabled]="draftBusy || !canAuthor()"
          >
            {{ draftBusy ? "Creating…" : "Create draft" }}</button
          ><button
            uiButton
            type="button"
            (click)="closeDraft()"
            [disabled]="draftBusy"
          >
            Cancel
          </button>
        </div>
      </form>
      <section
        *ngIf="draft && draftDefinition as definition"
        class="calendar-draft"
        aria-labelledby="calendar-draft-title"
      >
        <header class="calendar-header">
          <div>
            <h3 id="calendar-draft-title">
              {{
                draft.statusCode === "published"
                  ? "Published calendar version"
                  : "Calendar draft"
              }}
              · {{ definition.label }}
            </h3>
            <p>
              {{ definition.code }} / {{ definition.versionCode }} · revision
              {{ draft.revision }}{{ draftDirty ? " · unsaved changes" : "" }}
            </p>
          </div>
          <span class="calendar-posture">{{ draft.statusCode }}</span>
        </header>
        <p class="calendar-boundary">
          {{
            draft.statusCode === "published"
              ? "This version is locked. Select it below and create the next version to make further changes."
              : "Draft edits do not affect published versions. Publish a saved draft to make its exact version available for explicit selection."
          }}
          Existing policies and recorded evidence keep their selected versions.
        </p>
        <form (ngSubmit)="saveDraft()">
          <fieldset class="calendar-draft-fields" [disabled]="!canEditDraft">
            <label
              >Calendar name
              <input
                uiControl
                name="calendarLabel"
                [(ngModel)]="definition.label"
                (ngModelChange)="draftChanged()"
                maxlength="160"
            /></label>
            <label
              >Time zone
              <select
                uiControl
                name="timeZoneId"
                [(ngModel)]="definition.timeZoneId"
                (ngModelChange)="draftChanged()"
              >
                <option value="">Select an installed time zone</option>
                <option
                  *ngIf="definition.timeZoneId && !knownDraftTimeZone"
                  [value]="definition.timeZoneId"
                >
                  {{ definition.timeZoneId }} · unavailable retained choice
                </option>
                <option *ngFor="let zone of timeZones" [value]="zone.id">
                  {{ zone.label }}
                </option>
              </select>
              <small *ngIf="!knownDraftTimeZone"
                >This draft's retained time zone is unavailable. Select an
                installed time zone before publishing.</small
              >
            </label>
            <label
              >Effective from (inclusive local date)
              <input
                uiControl
                type="date"
                name="effectiveFrom"
                [(ngModel)]="definition.effectiveFrom"
                (ngModelChange)="draftChanged()"
            /></label>
            <label
              >Effective to (exclusive local date, optional)
              <input
                uiControl
                type="date"
                name="effectiveTo"
                [(ngModel)]="definition.effectiveTo"
                (ngModelChange)="draftChanged()"
            /></label>
            <fieldset class="calendar-working-days">
              <legend>Working days</legend>
              <label *ngFor="let day of weekdays"
                ><input
                  type="checkbox"
                  [name]="'working-' + day"
                  [checked]="definition.workingDays.includes(day)"
                  (change)="toggleWorkingDay(day, $any($event.target).checked)"
                />{{ day | titlecase }}</label
              >
            </fieldset>
            <section
              class="calendar-holiday-editor"
              aria-label="Draft holiday dates"
            >
              <label
                >Holiday date
                <input
                  uiControl
                  type="date"
                  name="holidayDate"
                  [(ngModel)]="holidayDate"
              /></label>
              <button
                uiButton
                type="button"
                (click)="addHoliday()"
                [disabled]="!holidayDate || !canEditDraft"
              >
                Add holiday
              </button>
              <ul *ngIf="definition.holidays.length">
                <li *ngFor="let date of definition.holidays">
                  <time [attr.datetime]="date">{{ date }}</time
                  ><button
                    uiButton
                    type="button"
                    (click)="removeHoliday(date)"
                    [attr.aria-label]="'Remove holiday ' + date"
                    [disabled]="!canEditDraft"
                  >
                    Remove
                  </button>
                </li>
              </ul>
              <p *ngIf="!definition.holidays.length" class="calendar-boundary">
                No holiday exclusions.
              </p>
              <small>Dates are stored without a time or UTC conversion.</small>
            </section>
          </fieldset>
          <div class="calendar-actions">
            <button
              *ngIf="canAuthor() && draft.statusCode === 'draft'"
              uiButton
              type="submit"
              [disabled]="!canSaveDraft"
            >
              {{ draftBusy ? "Working…" : "Save draft" }}</button
            ><button
              *ngIf="canPublish() && draft.statusCode === 'draft'"
              uiButton
              type="button"
              severity="primary"
              [outlined]="false"
              (click)="publishDraft()"
              [disabled]="!canPublishDraft"
            >
              Publish version {{ definition.versionCode }}</button
            ><button
              uiButton
              type="button"
              *ngIf="draft.statusCode === 'draft'"
              (click)="reloadDraft()"
              [disabled]="draftBusy || loadingDrafts"
            >
              Reload saved draft</button
            ><button
              uiButton
              type="button"
              (click)="closeDraft()"
              [disabled]="draftBusy"
            >
              Close draft
            </button>
          </div>
          <p
            *ngIf="draftDirty && draft.statusCode === 'draft'"
            class="calendar-boundary"
          >
            Save these changes before publishing.
          </p>
          <p
            *ngIf="draft.statusCode === 'draft' && !canAuthor()"
            class="calendar-boundary"
          >
            Calendar author permission is required to edit or save this draft.
          </p>
        </form>
      </section>
      <p *ngIf="draftValidationError" class="calendar-error" role="alert">
        {{ draftValidationError }}
      </p>
      <p *ngIf="draftStale" class="calendar-notice" role="status">
        This saved draft is stale. Your local entries are kept. Reload the saved
        draft to review its current revision before retrying.
      </p>
      <section *ngIf="draftError as error" class="calendar-error" role="alert">
        <strong>{{ error.title }}</strong>
        <ul>
          <li *ngFor="let message of error.messages">{{ message }}</li>
        </ul>
        <p>Your local entries are kept.</p>
        <details *ngIf="error.problemCode || error.issueCodes?.length">
          <summary>Calendar issue details</summary>
          <p>{{ error.problemCode }}</p>
          <p>{{ error.issueCodes?.join(", ") }}</p>
        </details>
      </section>
      <p
        *ngIf="draftNotice"
        class="calendar-notice"
        role="status"
        aria-live="polite"
      >
        {{ draftNotice }}
      </p>
    </section>
    <label class="calendar-picker"
      >Calendar version
      <select
        uiControl
        [(ngModel)]="selectedKey"
        (ngModelChange)="selectCalendar()"
        [disabled]="loadingCatalog || calculating"
      >
        <option value="">Select a calendar version</option>
        <option *ngIf="selectedKey && !selected" [value]="selectedKey" disabled>
          Previously selected version · unavailable
        </option>
        <option *ngFor="let calendar of calendars" [value]="identity(calendar)">
          {{ calendar.label }} · {{ calendar.versionCode }}
        </option>
      </select></label
    >
    <p *ngIf="!loadingCatalog && !calendars.length && !catalogError">
      No business calendar versions are installed.
    </p>
    <div class="calendar-layout" *ngIf="selected as calendar">
      <section
        class="calendar-definition"
        aria-label="Selected calendar version"
      >
        <header>
          <h3>{{ calendar.label }}</h3>
          <span class="calendar-posture">{{ posture(calendar) }}</span>
        </header>
        <button
          *ngIf="calendar.managed && canAuthor()"
          uiButton
          type="button"
          (click)="editSelected()"
          [disabled]="draftBusy"
        >
          Edit next version / resume draft
        </button>
        <p *ngIf="!calendar.managed" class="calendar-boundary">
          Installed reference calendar · read only. Create a managed calendar to
          configure your own working days and holidays.
        </p>
        <dl>
          <div>
            <dt>Exact version</dt>
            <dd>{{ calendar.code }} / {{ calendar.versionCode }}</dd>
          </div>
          <div>
            <dt>Time zone</dt>
            <dd>{{ calendar.timeZoneId }}</dd>
          </div>
          <div>
            <dt>Effective local dates</dt>
            <dd>
              {{ calendar.effectiveFrom }} inclusive →
              {{ calendar.effectiveTo || "no end date"
              }}{{ calendar.effectiveTo ? " exclusive" : "" }}
            </dd>
          </div>
          <div>
            <dt>Working days</dt>
            <dd>{{ workingDays(calendar) }}</dd>
          </div>
        </dl>
        <p class="calendar-boundary">
          {{
            calendar.posture.includes("synthetic")
              ? "Synthetic calculation fixture. This calendar is not an approved hearing policy."
              : calendar.posture.includes("compatibility")
                ? "Compatibility calendar. It preserves existing date behavior; it does not approve a new hearing policy."
                : "Installed calendar configuration. A calculation does not select a production hearing deadline rule."
          }}
        </p>
        <p class="calendar-boundary">
          Local times in a daylight-saving gap are rejected. Repeated local
          times require an explicit valid UTC offset.
        </p>
        <details *ngIf="calendar.holidays.length; else noHolidays">
          <summary>
            {{ calendar.holidays.length }} configured holiday dates
          </summary>
          <div class="holiday-grid">
            <ui-grid
              [rowData]="holidayRows"
              [columnDefs]="holidayColumns"
              [agGridOptions]="holidayOptions"
              [showPanelHeader]="false"
              [showStandardFilterControls]="false"
              [showBlankRowsOnLastPage]="false"
              [autoHidePaginator]="true"
              containerClass="no-panel"
              width="fill"
            />
          </div>
        </details>
        <ng-template #noHolidays
          ><p>
            No holidays are excluded by this exact calendar version.
          </p></ng-template
        >
        <details>
          <summary>Calendar content identity</summary>
          <code class="calendar-hash">{{ calendar.contentHash }}</code>
        </details>
      </section>

      <section
        class="calendar-calculator"
        aria-labelledby="calendar-calculation-title"
      >
        <h3 id="calendar-calculation-title">Calculation preview</h3>
        <p class="calendar-boundary">
          No deadline, timer, booking or workflow is changed.
        </p>
        <form (ngSubmit)="calculate()">
          <fieldset [disabled]="calculating || loadingCatalog">
            <label
              >Operation
              <select
                uiControl
                name="operation"
                [(ngModel)]="operation"
                (ngModelChange)="inputChanged()"
              >
                <option *ngFor="let choice of operations" [value]="choice.code">
                  {{ choice.label }}
                </option>
              </select></label
            >
            <label *ngIf="operation !== 'instant-to-local'"
              >{{
                isDateAddition
                  ? "Starting date (date only)"
                  : "Local calendar date"
              }}
              <input
                uiControl
                type="date"
                name="date"
                [(ngModel)]="date"
                (ngModelChange)="inputChanged()"
                required
            /></label>
            <label *ngIf="isDateAddition"
              >Days to add
              <input
                uiControl
                type="number"
                step="1"
                name="amount"
                [(ngModel)]="amount"
                (ngModelChange)="inputChanged()"
                required
              /><small
                >Negative values count backwards. The starting date is not
                counted.</small
              ></label
            >
            <ng-container *ngIf="operation === 'local-to-instant'">
              <label
                >Local time in {{ calendar.timeZoneId }}
                <input
                  uiControl
                  type="time"
                  step="1"
                  name="time"
                  [(ngModel)]="time"
                  (ngModelChange)="inputChanged()"
                  required
              /></label>
              <label
                >Explicit UTC offset (minutes)
                <input
                  uiControl
                  type="number"
                  step="1"
                  min="-840"
                  max="840"
                  name="explicitOffsetMinutes"
                  [(ngModel)]="explicitOffsetMinutes"
                  (ngModelChange)="inputChanged()"
                /><small
                  >Leave blank for an unambiguous time. For a repeated Pacific
                  time, −420 means UTC−07:00 and −480 means UTC−08:00.</small
                ></label
              >
            </ng-container>
            <label *ngIf="operation === 'instant-to-local'"
              >Scheduled instant (ISO 8601 with Z or offset)
              <input
                uiControl
                type="text"
                name="instant"
                [(ngModel)]="instant"
                (ngModelChange)="inputChanged()"
                placeholder="2026-11-01T08:30:00Z"
                required
            /></label>
          </fieldset>
          <p *ngIf="validationError" class="calendar-error" role="alert">
            {{ validationError }} Your inputs are kept.
          </p>
          <p *ngIf="staleSource" class="calendar-notice" role="status">
            This calendar source is stale. Refresh calendars and review the
            exact version before calculating again.
          </p>
          <button
            uiButton
            type="submit"
            severity="primary"
            [outlined]="false"
            [disabled]="blocked"
          >
            {{ calculating ? "Calculating…" : "Calculate preview" }}
          </button>
        </form>
        <section
          *ngIf="calculationError as error"
          class="calendar-error"
          role="alert"
        >
          <strong>{{ error.title }}</strong>
          <ul>
            <li *ngFor="let message of error.messages">{{ message }}</li>
          </ul>
          <p>
            Your inputs are kept. Correct them or retry when the service is
            available.
          </p>
          <details *ngIf="error.problemCode || error.issueCodes?.length">
            <summary>Calculation issue details</summary>
            <p>{{ error.problemCode }}</p>
            <p>{{ error.issueCodes?.join(", ") }}</p>
          </details>
        </section>
        <section
          *ngIf="result as receipt"
          class="calendar-result"
          aria-label="Calendar calculation result"
          aria-live="polite"
        >
          <h4>
            {{
              receipt.valueKind === "date-only"
                ? "Date-only result"
                : receipt.valueKind === "instant"
                  ? "Scheduled UTC instant"
                  : "Local date and time"
            }}
          </h4>
          <strong class="calendar-result-value"
            >{{
              receipt.valueKind === "instant" ? receipt.instant : receipt.date
            }}{{
              receipt.valueKind === "local-date-time" ? " " + receipt.time : ""
            }}</strong
          >
          <p>
            {{
              receipt.valueKind === "date-only"
                ? "A calendar date, with no implied midnight or UTC deadline."
                : "Resolved in " +
                  receipt.evidence.timeZoneId +
                  " · " +
                  offsetLabel(receipt.offsetMinutes)
            }}
          </p>
          <dl aria-label="Calculation provenance">
            <div>
              <dt>Calendar version</dt>
              <dd>
                {{ receipt.evidence.calendarCode }} /
                {{ receipt.evidence.calendarVersionCode }}
              </dd>
            </div>
            <div>
              <dt>Rule version</dt>
              <dd>
                {{ receipt.evidence.ruleCode }} /
                {{ receipt.evidence.ruleVersionCode }}
              </dd>
            </div>
            <div>
              <dt>Calculated at</dt>
              <dd>{{ receipt.evidence.calculatedAtUtc }}</dd>
            </div>
          </dl>
          <details>
            <summary>Exact source and calculation evidence</summary>
            <pre>{{ receipt.evidence | json }}</pre>
          </details>
        </section>
      </section>
    </div>
  </ng-container>
</section>
